import S, { DataSignal } from 's-js';
import { SArray as ArraySignal } from 's-array';
import { IAjax, Client } from 'lib/Ajax';
import { DataCacheCollection } from 'lib/DataCache';
import { AutoSavingDraftObject } from 'lib/autoSave';
import { ValidationWatcher } from 'lib/validationWatcher';

type DeleteFn<TItem, TData> = (client : Client<TItem, TData>, draft : TItem) => void;

export type OrderableLookupsParams<TItem, TData> = AddlessLookupParams<TItem, TData> | AddableLookupParams<TItem, TData>;

interface CommonLookupParams<TItem, TData> {
    items : ArraySignal<TItem>;
    orderProp : (item : TItem) => DataSignal<number>;
    itemKey : (item : TItem) => string | number;
    deleteFn? : DeleteFn<TItem, TData>;
}

interface AddlessLookupParams<TItem, TData> extends CommonLookupParams<TItem, TData> {
    supportsAdd : false;
}

interface AddableLookupParams<TItem, TData> extends CommonLookupParams<TItem, TData> {
    supportsAdd : true;
    addClient : Client<TItem, TData>;
    cacheCollection : DataCacheCollection<TItem, TData>;
    itemFactory : (order : number) => TItem;
}

export interface OrderableLookups<TItem> {
    Items : () => ItemViewModel<TItem>[];
    Add : () => void;
    OnSortItems : (item : ItemViewModel<TItem>, items : ItemViewModel<TItem>[]) => void;
    ItemKey : (item : TItem) => string | number;
    ShowDeleteBtn : boolean;
    SupportsAdd : boolean;
}

export interface ItemViewModel<TItem> {
    Model : TItem;
    Editing : DataSignal<boolean>;
    Delete : () => void;
    Validation : ValidationWatcher;
}

export function OrderableLookups<TItem extends { Url : () => string | null }, TData>(
    ajax : IAjax,
    params : OrderableLookupsParams<TItem, TData>
) : OrderableLookups<TItem> {
    const { items, orderProp, itemKey, deleteFn } = params,
        autoEditItem = { item : null as TItem | null },
        sortedItems = items.map(item => ItemViewModel(item, ajax, autoEditItem, deleteFn))
        .sort((item1, item2) => orderProp(item1.Model)() - orderProp(item2.Model)());

    return {
        Items: sortedItems,
        Add,
        OnSortItems,
        ItemKey: itemKey,
        ShowDeleteBtn: !!deleteFn,
        SupportsAdd: params.supportsAdd
    };

    function Add() {
        if (!params.supportsAdd) return;
        const { addClient, cacheCollection, itemFactory } = params;
        let req = addClient.Post(itemFactory(S.sample(NextOrder)));
        req.then(data => S.freeze(() =>
            // TH 12/12/2016 -- Run this line inside an S.freeze so that the
            // assignment to autoEditItem.item takes effect before the ItemViewModel
            // that will happen in the next tick (due to the SArray.map call above).
            autoEditItem.item = cacheCollection.update1(req.Date, data)
        ));
    }

    function NextOrder() {
        return items().length > 0 ?
            Math.max(...items().map(item => orderProp(item)())) + 1 : 0;
    }

    function OnSortItems(item : ItemViewModel<TItem>, items : ItemViewModel<TItem>[]) {
        S.freeze(() => {
            items.forEach((item, idx) => orderProp(item.Model)(idx));
        });
    }
}

function ItemViewModel<TItem extends { Url : () => string | null }, TData>(
    item : TItem,
    ajax : IAjax,
    autoEditItem : { item : TItem | null },
    deleteFn? : DeleteFn<TItem, TData>
) : ItemViewModel<TItem> {
    const client = ajax.Url<TItem, TData>(() => item.Url() || ''),
        draft = AutoSavingDraftObject(item, client),
        validation = ValidationWatcher(client),
        shouldAutoEdit = item === autoEditItem.item,
        editing = S.data(shouldAutoEdit);

    if (shouldAutoEdit) autoEditItem.item = null;

    return {
        Model: draft,
        Editing: editing,
        Delete,
        Validation: validation
    };

    function Delete() {
        deleteFn && deleteFn(client, draft);
    }
}


// WEBPACK FOOTER //
// ./apps/portcall/orderablelookups/model.ts